import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {ConfigService} from '@config/config.service';
import {CrmManagerScheduleDialogComponent} from '@crm/components/leads/crm-manager-schedule-dialog/crm-manager-schedule-dialog.component';
import {TaskStatusWorker} from '@crm/services/task-status.worker';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {ManagerTaskScheduleResponse} from '@services/associations/model/managerTaskScheduleResponse';
import {NameResponse} from '@services/associations/model/nameResponse';
import {ProjectContactResponse} from '@services/associations/model/projectContactResponse';
import {TaskChangeStatusRequest} from '@services/associations/model/taskChangeStatusRequest';
import {TaskGridResponse} from '@services/associations/model/taskGridResponse';
import {TaskRequest} from '@services/associations/model/taskRequest';
import {TaskResponse} from '@services/associations/model/taskResponse';
import {TaskScheduleResponse} from '@services/associations/model/taskScheduleResponse';
import {PermissionsModel} from '@shared/models/permission.model';
import {CrmService} from '@shared/services/crm.service';
import {SharedService} from '@shared/shared.service';
import {ValidateEmptyOrObject} from '@shared/validators/empty-or-object.validator';
import {ValidateIfObject} from '@shared/validators/if-object.validator';
import {BehaviorSubject, catchError, combineLatest, EMPTY, Observable, Subject, takeUntil} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import dayjs from "dayjs";
import * as selectFromLogin from '@login/ngrx/login.selectors';
import * as mainActions from '@main/ngrx/actions/main.actions';
import * as leadsBoardActions from '@crm/ngrx/actions/leads-board.actions';
import * as selectFromLeadsList from '@crm/ngrx/selectors/leads-list.selectors';
import * as selectFromLeadsBoard from '@crm/ngrx/selectors/leads-board.selectors';
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import StatusEnum = TaskChangeStatusRequest.StatusEnum;
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'crm-task-dialog',
    templateUrl: './crm-task-dialog.component.html',
    styleUrls: ['./crm-task-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CrmTaskDialogComponent implements OnInit, OnDestroy {
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    readonlyFlag: boolean;
    /** Current User */
    currentUser$: Observable<WebOwnProfileModel | null>;
    currentUser: WebOwnProfileModel | null;
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    taskForm: UntypedFormGroup;
    /** Min Date / Time */
    minDate = new Date();
    /** Scheduler */
    scheduler$ = new BehaviorSubject<TaskGridResponse[]>([]);
    endTime$ = new BehaviorSubject<TaskGridResponse[]>([]);
    /** Check if Close Dialog after Save */
    closeDialogFlag: boolean;
    /** Task From Store To Edit */
    taskFromStore$: Observable<TaskResponse | undefined>;
    savedData$: Subject<TaskResponse> = new Subject<TaskResponse>();
    projectContacts: NameResponse[] = [];
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    private disableDateCheck = false;

    private needUpdateDate = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            edit?: boolean,
            task?: TaskResponse,
            source?: string,
            contact?: NameResponse,
            project?: NameResponse,
            prevTask?: TaskResponse,
            projectContacts?: ProjectContactResponse[],
            type?: string
        },
        public dialogRef: MatDialogRef<CrmTaskDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private sharedService: SharedService,
        private translateService: TranslateService,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private crmService: CrmService,
        private actions$: Actions,
    ) {
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
        /** Select Current User */
        this.currentUser$ = this.store$.pipe(select(selectFromLogin.selectCurrentUser));
    }

    ngOnInit(): void {
        if (!this.taskForm) {
            this.initForm();
        }

        this.getPermissions();
        this.getCurrentUserFromObservable();

        combineLatest([this.permissions$, this.currentUser$]).pipe(
            filter(data => {
                return !!data[0] || !!data[1];
            }),
            take(1),
        ).subscribe(() => {
            if (this.currentUser?.id && this.permissions?.length) {
                this.patchManager();
            }
        });

        if (this.data.prevTask) {
            this.patchFormFromPrevTask(this.data.prevTask);
        }

        if (this.data.contact) {
            this.patchContact(this.data.contact);
        }

        if (this.data.project) {
            this.patchProject(this.data.project);
        }

        if (this.data.projectContacts) {
            this.data.projectContacts.map((item) => {
                // @ts-ignore
                this.projectContacts.push(item.contact);
            });

            if (this.projectContacts?.length) {
                this.patchContact(this.projectContacts[0]);
            }
        }

        /** Patch Form on Edit */
        if (this.data.edit && this.data.task && this.data.source) {
            /** Get Task From Store */
            this.getTaskFromStore(this.data.task);
            this.updateWidth();
        } else if (this.data.edit && this.data.task && !this.data.source) {
            this.patchForm(this.data.task);
            this.updateWidth();
        }
        /** Get Schedules on manager or Date Change */
        this.watchFormChanges();
        /** Watch Actions */
        this.watchSuccessActions();
        this.watchFailureActions();
    }

    ngOnDestroy() {
        this.loading$.complete();
        this.scheduler$.complete();
        this.endTime$.complete();
        this.savedData$.complete();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    updateWidth() {
        this.dialogRef.updateSize(this.config.rightSideDialogProps.width, this.config.rightSideDialogProps.height);
    }

    /** Get Permissions from Observable */
    getPermissions() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    /** Get Current User From Observable */
    getCurrentUserFromObservable() {
        this.currentUser$.pipe(
            filter(data => !!data),
            take(1),
        ).subscribe((currentUser: WebOwnProfileModel | null) => {
            this.currentUser = currentUser;
        });
    }

    /** Check Permissions for DOM Elements */
    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    checkPermissionAndOwner(permission: string, managerPermission: string, element: TaskResponse | undefined) {
        if (this.checkPermissions(permission)) {
            return false;
        }

        return !(this.checkPermissions(managerPermission) && element?.manager?.id === this.currentUser?.id);
    }

    initForm() {
        this.taskForm = this.formBuilder.group({
            manager: ['', [Validators.required, ValidateIfObject]],
            contact: ['', [ValidateEmptyOrObject]],
            project: ['', [ValidateEmptyOrObject]],
            type: [this.data?.type ? this.data.type : 'CALL', [Validators.required]],
            priority: ['MIDDLE', [Validators.required]],
            date: [new Date(), [Validators.required]],
            time: [''],
            endTime: [''],
            text: [''],
        });
    }

    /** Get Task From Store */
    getTaskFromStore(task: TaskResponse) {
        this.taskFromStore$ = this.data.source === 'BOARD' ?
            this.store$.pipe(select(selectFromLeadsBoard.selectTaskById(task.id!))) :
            this.store$.pipe(select(selectFromLeadsList.selectTaskById(task.id!)));

        this.taskFromStore$.pipe(
            takeUntil(this.destroyed$),
        ).subscribe((task: TaskResponse | undefined) => {
            this.data.task = task;
            if (task) {
                this.patchForm(task);
            }
        });
    }

    /** Patch Form on Edit */
    patchForm(task: TaskResponse) {
        if (!this.taskForm) {
            this.initForm();
        }
        this.taskForm.patchValue({
            ...task.manager && {manager: task.manager},
            ...task.project && {project: {id: task.project.id, name: task.project.name}},
            ...task.type && {type: task.type},
            ...task.priority && {priority: task.priority},
            ...task.date && {date: dayjs(this.needUpdateDate ? this.minDate : task.date).toDate()},
            ...task.time && {time: task.time},
            ...task.endTime && {endTime: task.endTime},
            ...task.text && {text: task.text},
            ...task && task.contact && {
                contact:
                    {
                        id: task.contact.id,
                        name: `${task.contact.lastName} ${task.contact.firstName} ${task.contact?.parentalName ? task.contact.parentalName : ''}`,
                        description: (task.contact.phones && task.contact.phones?.length) ? task.contact.phones.join(', ') : ''
                    }
            }
        });

        this.needUpdateDate = false;
    }

    /** Patch Form from previous task */
    patchFormFromPrevTask(task: TaskResponse) {
        if (!this.taskForm) {
            this.initForm();
        }

        this.taskForm.patchValue({
            ...task.contact && {
                contact: {
                    id: task.contact.id,
                    name: `${task.contact.lastName} ${task.contact.firstName}`,
                    description: (task.contact.phones && task.contact.phones?.length) ? task.contact.phones.join(', ') : ''
                }
            },
            ...task.project && {project: {id: task.project.id, name: task.project.name}},
            ...task.date && {date: dayjs().toDate()},
        });
    }

    patchManager() {
        if (!this.taskForm) {
            this.initForm();
        }

        this.taskForm.get('manager')?.setValue({id: this.currentUser?.id, name: this.currentUser?.name});
        this.getManagerSchedule(this.data.edit ? this.data.task?.date : '');

        if (this.data.edit && this.checkPermissionAndOwner('TASK_EDIT', 'TASK_OWN_EDIT', this.data.task)) {
            this.readonlyFlag = true;
            this.taskForm.get('date')?.disable();
            this.taskForm.get('time')?.disable();
            this.taskForm.get('endTime')?.disable();
        }

        if (!this.checkTypeEditAvailable()) {
            this.taskForm.get('type')?.disable();
        }
    }

    checkTypeEditAvailable() {
        if (!this.data.edit) {
            return true;
        }

        if (this.checkPermissions('TASK_EDIT')) {
            return true;
        }

        return !!(this.currentUser?.id && this.data.task?.author?.id && this.currentUser?.id === this.data.task?.author?.id);
    }

    patchContact(contact: NameResponse) {
        if (!this.taskForm) {
            this.initForm();
        }

        this.taskForm.get('contact')?.setValue({id: contact.id, name: contact.name, description: contact.description});
    }

    patchProject(project: NameResponse) {
        if (!this.taskForm) {
            this.initForm();
        }

        this.taskForm.get('project')?.setValue({id: project.id, name: project.name, description: project.description});
    }

    reschedule(day: string) {
        if (!this.taskForm.get('manager')?.value.id) {
            return;
        }

        this.disableDateCheck = true;
        this.taskForm.get('date')?.setValue(dayjs(day === 'today' ? dayjs().format() : dayjs().add(1, 'day')).toDate());
        this.taskForm.get('time')?.reset();
        this.disableDateCheck = false;
    }

    openManagerSchedule() {
        const managerScheduler$ = this.sharedService.openDialog(
            CrmManagerScheduleDialogComponent,
            {...this.config.wideDialog, width: '1000px', height: '570px'},
            {
                managerId: this.taskForm.get('manager')?.value.id,
                date: this.taskForm.get('date')?.value,
            }
        );

        managerScheduler$.afterClosed().pipe().subscribe(dialogData => {
            if (dialogData && dialogData.result && dialogData.data) {
                this.disableDateCheck = true;
                this.scheduler$.next(dialogData.data.schedule);
                this.timeSelected(dialogData.data.time);
                this.taskForm.get('date')?.setValue(dayjs(dialogData.data.day).toDate());
                this.taskForm.get('time')?.setValue(dialogData.data.time);
                if (dialogData.data.endTime && dialogData.data.time !== dialogData.data.endTime) {
                    this.taskForm.get('endTime')?.setValue(dialogData.data.endTime);
                }
                this.disableDateCheck = false;
            }
        });
    }

    /** Watch Form Changes */
    watchFormChanges() {
        if (!this.taskForm) {
            this.initForm();
        }

        this.taskForm.get('manager')!.valueChanges.pipe(
            takeUntil(this.destroyed$),
        ).subscribe(selectedValue => {
            if (typeof selectedValue === 'object' && selectedValue?.id !== this.taskForm.value['manager']?.id) {
                this.taskForm.get('date')?.setValue(dayjs().toDate());
                this.taskForm.get('time')?.reset();
                this.taskForm.get('endTime')?.reset();
            }
        });

        this.taskForm.get('date')!.valueChanges.pipe(
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            if (!this.disableDateCheck) {
                this.taskForm.get('time')?.reset();
                this.taskForm.get('endTime')?.reset();
                this.getManagerSchedule();
            }
        });
    }

    getManagerSchedule(taskDate?: string) {
        const managerId = this.taskForm.get('manager')!.value ? this.taskForm.get('manager')!.value.id : null;
        const date = taskDate ? taskDate : dayjs(this.taskForm.get('date')!.value).format('YYYY-MM-DD');

        if (!managerId || !date) {
            return;
        }

        this.loading$.next(true);
        this.crmService.loadManagerSchedule(managerId, date).pipe(
            takeUntil(this.destroyed$),
            map((response: ManagerTaskScheduleResponse) => {
                if (response.taskGrid) {
                    this.scheduler$.next(response.taskGrid);
                    if (this.data.task?.time && taskDate) {
                        this.timeSelected(this.data.task?.time, true);
                    }
                } else {
                    this.scheduler$.next([]);
                    this.endTime$.next([]);
                }
                this.loading$.next(false);
            }),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    timeSelected(value?: string, patchValue?: boolean) {
        if (!value) {
            return;
        }

        this.endTime$.next([]);
        this.taskForm.get('endTime')?.reset();

        let filteredHourMinutes: TaskGridResponse[] | TaskScheduleResponse[] = [];

        const hour = Number(value?.substr(0, 2));
        const availableEndHours = this.scheduler$.getValue().filter(item => hour - 1 < Number(item.hour));
        const minutesOfHour = availableEndHours.filter(item => item.hour === hour)[0];
        if (minutesOfHour && minutesOfHour.tasks?.length) {
            filteredHourMinutes = minutesOfHour.tasks.filter(item => Number(item.from!.replace(':', '')) > Number(value.replace(':', '')));
        }

        if (filteredHourMinutes?.length) {
            const index = availableEndHours.findIndex(item => item.hour === hour);
            if (index !== -1) {
                // @ts-ignore
                availableEndHours.splice(index, 1, {hour: hour, tasks: filteredHourMinutes});
            }
            this.endTime$.next(availableEndHours);
        } else {
            this.endTime$.next(this.scheduler$.getValue().filter(item => hour < Number(item.hour)));
        }

        if (patchValue && this.data.task?.endTime) {
            this.taskForm.get('endTime')?.setValue(this.data.task?.endTime);
        }
    }

    changeStatus(newStatus: StatusEnum) {
        if (this.readonlyFlag || !this.data.task) {
            return;
        }

        if (dayjs(dayjs(this.data.task.date).endOf('day')).isBefore(dayjs(this.minDate))) {
            this.needUpdateDate = true;
            if (this.data.edit && this.data.task && !this.data.source) {
                this.taskForm.patchValue({
                    date: dayjs(this.minDate).toDate()
                });
            }
        }

        TaskStatusWorker.changeTaskStatus(
            this.store$,
            this.sharedService,
            this.config,
            this.translateService,
            {
                task: this.data.task,
                status: newStatus,
                prevStatus: this.data.task.status!
            },
            newStatus);
    }

    /** Add or Edit Association */
    submitForm(closeDialog: boolean) {
        this.closeDialogFlag = closeDialog;
        this.loading$.next(true);
        const formData = this.taskForm.getRawValue();

        const request: TaskRequest = {
            ...formData.contact && {contactId: formData.contact.id},
            ...formData.project && {projectId: formData.project.id},
            ...formData.date && {date: dayjs(formData.date).format('YYYY-MM-DD')},
            ...formData.priority && {priority: formData.priority},
            ...formData.manager && {managerId: formData.manager.id},
            ...formData.text && {text: formData.text},
            ...formData.time && {time: formData.time},
            ...formData.endTime && {endTime: formData.endTime},
            ...formData.type && {type: formData.type},
            ...this.data.prevTask && this.data.prevTask.id && {allowCloseTaskId: this.data.prevTask.id}
        };

        if (!this.data.edit && !this.data.task) {
            this.store$.dispatch(new leadsBoardActions.AddTasks(request));
        } else {
            this.store$.dispatch(new leadsBoardActions.EditTasks({taskId: this.data.task!.id!, request}));
        }
    }

    call(number: string) {
        this.store$.dispatch(new mainActions.ProceedCall({command: 'call', number: number}));
    }

    /** Watch Actions */
    watchSuccessActions() {
        this.actions$.pipe(
            ofType(
                leadsBoardActions.LeadsBoardActionTypes.AddTasksSuccess,
                leadsBoardActions.LeadsBoardActionTypes.EditTasksSuccess,
            ),
            takeUntil(this.destroyed$),
        ).subscribe((action: leadsBoardActions.AddTasksSuccess | leadsBoardActions.EditTasksSuccess) => {
            if (action.isSocket && action.type === '[LeadsBoard] Add Tasks Success') {
                return;
            }

            if (action.type === '[LeadsBoard] Add Tasks Success' && (this.data.edit || this.data.task)) {
                return;
            }

            if (action.type === '[LeadsBoard] Edit Tasks Success' && action.payload?.id !== this.data.task?.id) {
                return;
            }

            this.loading$.next(false);
            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                if (!this.data.edit) {
                    this.data.edit = true;
                    this.updateWidth();
                }
                this.data.task = action.payload;
            }

            this.savedData$.next(action.payload);
        });
    }

    watchFailureActions() {
        this.actions$.pipe(
            ofType(
                leadsBoardActions.LeadsBoardActionTypes.AddTasksFailure,
                leadsBoardActions.LeadsBoardActionTypes.EditTasksFailure,
            ),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    /** Minimize Dialog */
    minimize() {
        SharedService.minimize(
            this.dialogRef.id,
            `${dayjs(this.data.task?.date).format('DD.MM.YYYY')} ${this.data.task?.manager?.name}`,
            this.store$,
            this.data.task?.id,
            false,
            '',
            `${SharedService.getTranslation(this.translateService,
                'modules.crm.leads.taskDialog.title')} - ${dayjs(this.data.task?.date).format('DD.MM.YYYY')} ${this.data.task?.manager?.name}`,
        );
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
        this.store$.dispatch(new mainActions.CloseMinimizedDialog(this.dialogRef.id));
    }

    /** workDaysOnly  */
    weekdaysFilter = (d: Date | null): boolean => {
        if (!d) {
            return false;
        }

        const day = d.getDay();
        return day !== 0 && day !== 6;
    };
}
